import { Store } from '@ngrx/store';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Camera, HealthCheck, HealthHistory, HealthPanel } from '../models';
import { AnimationOptions } from 'ngx-lottie';
import { AppState } from 'app/store/model';
import { CameraActions } from '../Services/actions';
import { MatAccordion } from '@angular/material/expansion';
import { UntypedFormControl } from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { formatDate } from '@angular/common';

@Component({
    selector: 'app-camera-health',
    templateUrl: './camera-health.component.html',
    styleUrls: ['./camera-health.component.scss'],
    standalone: false,
})
export class CameraHealthComponent implements OnInit, OnDestroy, AfterViewInit {
    readonly health$ = this.store.select(
        (state: AppState) => state.camera.connectionHealth
    );
    readonly history$ = this.store.select(
        (state: AppState) => state.camera.connectionHistory
    );
    readonly finalPage$ = this.store.select(
        (state: AppState) => state.camera.finalPage
    );
    readonly loading$ = this.store.select(
        (state: AppState) => state.camera.historyLoading
    );
    @Input() camera: Camera;
    @ViewChild(MatAccordion) accordion: MatAccordion;
    dateFilter = new UntypedFormControl('');
    healthStatus: HealthCheck;
    sub: Subscription;
    options: AnimationOptions = {
        path: '',
    };
    styles: Partial<CSSStyleDeclaration> = {
        width: '10rem',
        height: '10rem',
        marginLeft: 'auto',
        marginRight: 'auto',
    };
    page = 1;
    filtering = false;
    loading = false;
    @ViewChild('picker') picker: MatDatepicker<Date>;

    constructor(private readonly store: Store<AppState>) { }

    ngOnInit(): void {
        this.store.dispatch(
            CameraActions.get_healthcheck({
                camera_id: this.camera.id,
                user_sub: localStorage.getItem('sub'),
            })
        );
        this.loading = true;
        this.getHealth();
        this.store.dispatch(
            CameraActions.get_health_history({
                camera_id: this.camera.id,
                user_sub: localStorage.getItem('sub'),
                page: this.page,
            })
        );
    }

    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
        this.store.dispatch(CameraActions.clear_history());
        this.store.dispatch(CameraActions.get_healthcheck_cleanup());
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.nuclearSolution();
        }, 350);
    }

    getAnimationPath(): string {
        if (this.healthStatus) {
            const p = document.getElementById('status-text');
            if (p) {
                switch (this.healthStatus.current_status.toLowerCase()) {
                    case 'ruim':
                        p.classList.add('bad');
                        return '/assets/animations/weather-thunder.json';
                    case 'instável':
                        p.classList.add('unstable');
                        return '/assets/animations/weather-mist.json';
                    case 'estável':
                        p.classList.add('stable');
                        return '/assets/animations/weather-partly-cloudy.json';
                }
            }
        } else {
            return null;
        }
    }

    getHealth() {
        this.sub = this.health$.subscribe((result) => {
            if (result) {
                this.loading = false;
                this.healthStatus = result;
                setTimeout(() => {
                    this.options = {
                        ...this.options,
                        path: this.getAnimationPath(),
                    };
                }, 1500);
            }
        });
    }

    //Pega o tempo em segundos e retorna uma string no formato "HH:MM:ss"
    formatDownTime(time: number): string {
        const hours = Math.floor(time / 3600);
        const minutes = Math.floor((time % 3600) / 60);
        const seconds = Math.floor((time % 3600) % 60);

        return (
            ('0' + hours).slice(-2) +
            ':' +
            ('0' + minutes).slice(-2) +
            ':' +
            ('0' + seconds).slice(-2)
        );
    }

    getHours(time: number): string {
        const hours = Math.floor(time / 3600);
        return hours.toString();
    }

    getTextClass(status?: string) {
        let statusText;
        if (!status && this.healthStatus) {
            statusText = this.healthStatus.current_status;
        } else if (status) {
            statusText = status;
        }
        if (statusText) {
            switch (statusText.toLowerCase()) {
                case 'ruim':
                    return 'bad';
                case 'instável':
                    return 'unstable';
                case 'estável':
                    return 'stable';
            }
        }
    }

    onScroll() {
        if (this.filtering) {
            const date = this.dateFilter.value;
            const beginDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                0,
                0,
                0,
                0
            );
            const endDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                23,
                59,
                59,
                59
            );
            this.store.dispatch(
                CameraActions.get_health_history_filter({
                    camera_id: this.camera.id,
                    user_sub: localStorage.getItem('sub'),
                    page: ++this.page,
                    timeBegin: formatDate(beginDate, 'yyyy-MM-dd HH:mm:ss', 'pt'),
                    timeEnd: formatDate(endDate, 'yyyy-MM-dd HH:mm:ss', 'pt'),
                })
            );
        } else {
            this.store.dispatch(
                CameraActions.get_health_history({
                    camera_id: this.camera.id,
                    user_sub: localStorage.getItem('sub'),
                    page: ++this.page,
                })
            );
        }
    }

    itemList(panel: HealthPanel): string {
        const len = panel.events.length;
        if (len > 1) {
            return `${len} itens`;
        } else {
            return `${len} item`;
        }
    }

    equalDate(panel: HealthPanel): boolean {
        if (panel.endDate === null) {
            return true;
        } else {
            const timeDiff = panel.endDate.valueOf() - panel.startDate.valueOf();
            return Math.floor(timeDiff / 1000 / 3600) < 24;
        }
    }

    hasGap(events: HealthHistory[], i: number): string {
        return i + 1 < events.length ? 'bottom-gap' : '';
    }

    onDateInputFilter = (date: Date | null): boolean => {
        if (!date) { return false; }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const daysInterval = Number(this.camera.interval_delete_camera_status_history_in_days) || 7;

        const fromDaysAgo = new Date(today);
        fromDaysAgo.setHours(0, 0, 0, 0);
        fromDaysAgo.setDate(today.getDate() - daysInterval);


        return date >= fromDaysAgo && date <= today;
    };


    onDateInput(event: MatDatepickerInputEvent<Date>) {
        this.store.dispatch(CameraActions.clear_history());
        this.page = 0;
        this.filtering = true;
        const date = event.value;
        const today = new Date();
        if (
            today >= new Date(date.getFullYear(), date.getMonth(), date.getDate())
        ) {
            this.dateFilter.setValue(event.value);
            const beginDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                0,
                0,
                0,
                0
            );
            const endDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                23,
                59,
                59,
                59
            );
            this.store.dispatch(
                CameraActions.get_health_history_filter({
                    camera_id: this.camera.id,
                    user_sub: localStorage.getItem('sub'),
                    page: ++this.page,
                    timeBegin: formatDate(beginDate, 'yyyy-MM-dd HH:mm:ss', 'pt'),
                    timeEnd: formatDate(endDate, 'yyyy-MM-dd HH:mm:ss', 'pt'),
                })
            );
        }
    }

    goBack() {
        this.store.dispatch(CameraActions.clear_history());
        this.page = 0;
        this.filtering = false;
        this.dateFilter.setValue('');
        this.store.dispatch(
            CameraActions.get_health_history({
                camera_id: this.camera.id,
                user_sub: localStorage.getItem('sub'),
                page: ++this.page,
            })
        );
    }

    private nuclearSolution() {
        const style = document.createElement('style');
        style.id = 'datepicker-force-styles';
        style.innerHTML = `
          /* Estilo para datas desabilitadas */
          .mat-datepicker-content .mat-calendar-body-disabled {
            background-color: #F3F9FF !important;
          }

          
          /* Tooltip customizado - versão absoluta */
          .mat-datepicker-content .mat-calendar-body-disabled:hover::after {
            content: 'Histórico não disponível' !important;
            position: absolute !important;
            width: 182px !important;
            height: 32px !important;
            border-radius: 8px !important;
            padding: 8px 16px !important;
            gap: 10px !important;
            background: #002D59 !important;
            color: white !important;
            font-size: 12px !important;
            z-index: 9999 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            opacity: 1 !important;
            top: -40px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
          }
        `;

        document.head.appendChild(style);
    }
}
